import { Typography } from '@mui/material';
import moment from 'moment';
import {
	AutocompleteArrayInput,
	AutocompleteInput,
	ChipField,
	Datagrid,
	DateField,
	FunctionField,
	NumberField,
	ReferenceArrayField,
	ReferenceArrayInput,
	ReferenceField,
	ReferenceInput,
	SelectArrayInput,
	SelectInput,
	SingleFieldList,
	TextField,
	useListContext,
	usePermissions,
	useTranslate,
} from 'react-admin';
import { CustomizableDatagrid } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import { CPFField } from '../../components/Fields';
import Permission from '../../components/Permission';
import ReportList from '../../components/ReportList';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';
import { ProductPointsType } from '../../models';
import { formatNumber, formatPoints, filterToQuery } from '../../utils';

const titleSx = {
	margin: '10px 0 10px 0',
	textAlign: 'center',
	fontWeight: 'bold'
};

const formatCurrency = value => formatNumber(Number(value) || 0, {
	style: 'currency',
	currency: 'BRL',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2
});

const formatAmount = value => formatNumber(Number(value) || 0, {
	minimumFractionDigits: 0,
	maximumFractionDigits: 3
});

const ProductPointsList = () => {
	const props = useListContext();
	const { permissions } = usePermissions();
	const isAdmin = permissions && permissions.includes(PERMISSIONS.ADMIN);

	let itemsData = [];
	let summaryData = [];

	if (props.data) {
		const reportInfo = props.data[0];

		if (reportInfo) {
			itemsData = reportInfo.data;
			summaryData = reportInfo.summaryData;
		}
	}

	return (
		<>
			<Typography sx={titleSx}>Totais por Produto</Typography>
			<Datagrid bulkActionButtons={false} style={{ marginBottom: 60 }} data={summaryData} total={summaryData.length} hover={false}>
				<TextField source="product_name" label="Produto" textAlign="center" sortable={false} />
				<FunctionField
					label="Tipo"
					textAlign="center"
					sortable={false}
					render={record => record.id === "0" ? "Todos" : ProductPointsType.translateType(record.product_type)}
				/>
				<FunctionField
					label="Quantidade"
					textAlign="center"
					sortable={false}
					render={record => formatAmount(record.total_amount)}
				/>
				<FunctionField
					label="Valor"
					textAlign="center"
					sortable={false}
					render={record => formatCurrency(record.total_value)}
				/>
				<FunctionField
					label="Total de Pontos"
					emptyText="0"
					textAlign="center"
					sortable={false}
					render={record => formatPoints(record.total_points)}
				/>
				<NumberField source="total_items" label="Itens" textAlign="center" emptyText="0" sortable={false} />
				<NumberField source="total_sales" label="Vendas" textAlign="center" emptyText="0" sortable={false} />
			</Datagrid>

			<Typography sx={titleSx}>Lista Detalhada</Typography>
			<CustomizableDatagrid
				bulkActionButtons={false}
				resource={props.resource}
				data={itemsData}
				hover={false}
			>
				<TextField label="Nome" source="customer_name" emptyText="Não cadastrado" textAlign="center" sortable={false} />
				<CPFField source="customer_cpf" label="CPF" textAlign="center" sortable={false} />
				<TextField label="Produto" source="product_name" emptyText="Não identificado" textAlign="center" sortable={false} />
				<FunctionField
					label="Tipo"
					textAlign="center"
					sortable={false}
					render={record => ProductPointsType.translateType(record.product_type)}
				/>
				<TextField label="Código" source="product_code" emptyText="-" textAlign="center" sortable={false} />
				<FunctionField
					label="Quantidade"
					textAlign="center"
					sortable={false}
					render={record => formatAmount(record.amount)}
				/>
				<FunctionField
					label="Preço Unitário"
					textAlign="center"
					sortable={false}
					render={record => formatCurrency(record.unit_price)}
				/>
				<FunctionField
					label="Valor"
					textAlign="center"
					sortable={false}
					render={record => formatCurrency(record.value)}
				/>
				<FunctionField
					label="Pontos"
					emptyText="0"
					textAlign="center"
					sortable={false}
					render={record => formatPoints(record.points)}
				/>
				<ReferenceField
					label="Local"
					textAlign="center"
					emptyText="Não identificado"
					source="place_id"
					reference="places"
					link="show"
					perPage={null}
					sortable={false}
				>
					<TextField source="name" />
				</ReferenceField>
				<ReferenceField
					label="Frentista"
					textAlign="center"
					emptyText="Não identificado"
					source="attendant_id"
					reference="attendants"
					perPage={null}
					sortable={false}
				>
					<TextField source="name" />
				</ReferenceField>
				{isAdmin &&
					<ReferenceField
						label="Parceiro"
						textAlign="center"
						emptyText="Não identificado"
						source="external_partner_id"
						reference="external-partners"
						perPage={null}
						sortable={false}
					>
						<TextField source="name" />
					</ReferenceField>
				}
				<FunctionField
					label="Pontuou"
					textAlign="center"
					sortable={false}
					render={record => record.has_transaction ? 'Sim' : 'Não'}
				/>
				<TextField label="Nº da Venda no Parceiro" source="sale_external_id" emptyText="-" textAlign="center" sortable={false} />
				<DateField source="created_at" label="Data" showTime sortable={false} locales="pt-BR" />
			</CustomizableDatagrid>
		</>
	);
};

// Local e frentista saem da API como id: o nome vem por lazy load aqui, do mesmo jeito que o
// ReferenceField faz na tela, pra query do relatório não precisar carregar `place` e `attendant`.
//
// `external-partners` só entra na exportação pra admin: o endpoint é `scope: ['isAdmin']` e o
// fetchRelatedRecords roda tudo num Promise.all — se um gerente de posto exportasse com essa
// relação incluída, a chamada tomaria 403 e derrubaria a exportação inteira, não só a coluna.
const makeProductPointsExporter = (isAdmin) => async (data, fetchRelatedRecords) => {
	let items = data[0].data;

	const relations = [
		{ field: 'place_id', resource: 'places' },
		{ field: 'attendant_id', resource: 'attendants' },
	];
	if (isAdmin) { relations.push({ field: 'external_partner_id', resource: 'external-partners' }); }

	const fetched = await fetchRelatedRecords(relations, items);

	const getData = (resource) => {
		return fetched.find(({ resource: res }) => res === resource)?.results || [];
	};

	items = items.map(item => {
		const { place_id, id, sale_id, product_id, attendant_id, external_partner_id, has_transaction, product_type, ...itemRest } = item;
		const place = getData('places').find((place) => place.id === place_id);
		const attendant = getData('attendants').find((attendant) => attendant.id === attendant_id);
		const externalPartner = isAdmin && getData('external-partners').find((partner) => partner.id === external_partner_id);

		return {
			...itemRest,
			product_type: ProductPointsType.translateType(product_type),
			place_name: place ? place?.name : 'Não identificado',
			attendant_name: attendant ? attendant?.name : 'Não identificado',
			...(isAdmin ? { external_partner_name: externalPartner ? externalPartner?.name : 'Não identificado' } : {}),
			has_transaction: has_transaction ? 'Sim' : 'Não',
		};
	});

	return items;
};

const FilterTablePrint = () => {
	const { filterValues } = useListContext();
	return (
		<Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
			<DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
			<DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
			<ReferenceArrayField filter={{ chainId: getChainId() }} label="Locais" emptyText="Todos" source="stationIds" textAlign="center" reference="places" link={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<ReferenceArrayField label="Produtos" emptyText="Todos" source="productIds" textAlign="center" reference="chains/chainId/sale-products" link={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<ReferenceArrayField label="Combustíveis" emptyText="Todos" source="fuelIds" textAlign="center" reference="fuels" link={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<ReferenceArrayField label="Parceiros" emptyText="Todos" source="externalPartnerIds" textAlign="center" reference="external-partners" link={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<FunctionField
				label="Tipo"
				textAlign="center"
				render={record => record.productType ? ProductPointsType.translateType(record.productType) : 'Todos'}
			/>
			<DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
		</Datagrid>
	);
};

const ReportProductPoints = (props) => {
	const { permissions } = usePermissions();
	const translate = useTranslate();
	const isAdmin = permissions && permissions.includes(PERMISSIONS.ADMIN);

	const listFilters = [
		<BetweenDatesInput alwaysOn />,
		<ReferenceArrayInput
			filter={{ chainId: getChainId() }}
			source="stationIds"
			reference="places"
			perPage={null}
			sort={{ field: "name", order: "ASC" }}
			alwaysOn
		>
			<AutocompleteArrayInput
				filterToQuery={filterToQuery}
				label="Local"
				sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
				optionText={'name'}
				noOptionsText={translate('ra.input.autocomplete.no_options_text')}
			/>
		</ReferenceArrayInput>,
		<ReferenceArrayInput
			source="productIds"
			reference="chains/chainId/sale-products"
			perPage={null}
			sort={{ field: "name", order: "ASC" }}
			alwaysOn
		>
			<AutocompleteArrayInput
				filterToQuery={filterToQuery}
				label="Produto"
				sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
				optionText={'name'}
				noOptionsText={translate('ra.input.autocomplete.no_options_text')}
			/>
		</ReferenceArrayInput>,
		<ReferenceArrayInput
			source="fuelIds"
			reference="fuels"
			perPage={null}
			sort={{ field: "name", order: "ASC" }}
			alwaysOn
		>
			<SelectArrayInput style={{ minWidth: 180 }} label="Combustível" optionText="name" />
		</ReferenceArrayInput>,
		<SelectInput
			style={{ minWidth: 160 }}
			source="productType"
			label="Tipo"
			emptyText="Todos"
			choices={ProductPointsType.productPointsTypeChoices}
			alwaysOn
		/>
	];

	const stationsIndex = listFilters.findIndex(({ props }) => props.reference === 'places');
	const placeId = getPlaceId();

	if (placeId && stationsIndex > -1) {
		listFilters.splice(stationsIndex, 1);
	}

	if (isAdmin && stationsIndex > -1) {
		listFilters.splice(stationsIndex, 0,
			<ReferenceInput
				source="chainId"
				reference="chains"
				perPage={null}
				sort={{ field: "name", order: "ASC" }}
				alwaysOn
			>
				<AutocompleteInput
					filterToQuery={filterToQuery}
					style={{ minWidth: 180 }}
					optionText={'name'}
					label="Rede"
				/>
			</ReferenceInput>);
	}

	// Parceiro é dado do admin PontuaX (quem integra a API), não do gerente de posto — filtro e
	// coluna só existem pra quem tem acesso total.
	if (isAdmin) {
		listFilters.push(
			<ReferenceArrayInput
				source="externalPartnerIds"
				reference="external-partners"
				perPage={null}
				sort={{ field: "name", order: "ASC" }}
				alwaysOn
			>
				<AutocompleteArrayInput
					filterToQuery={filterToQuery}
					label="Parceiro"
					sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
					optionText={'name'}
					noOptionsText={translate('ra.input.autocomplete.no_options_text')}
				/>
			</ReferenceArrayInput>
		);
	}

	return (
		<Permission permission={PERMISSIONS.PRODUCT_POINTS_REPORT}>
			<ReportList periodRequired
				{...props}
				basePath="transactions"
				title="Pontos por Produto da Venda"
				resource="chains/chainId/reports/product-points"
				filters={listFilters}
				sort={{ field: 'created_at', order: 'DESC' }}
				filterDefaultValues={{
					stationIds: placeId ? [placeId] : null,
					chainId: getChainId(),
					from: moment().subtract(30, 'd').startOf('day').toISOString(),
					to: moment().endOf('day').toISOString()
				}}
				bulkActionButtons={false}
				customExporter={makeProductPointsExporter(isAdmin)}
				titleOnPrint="Relatório de Pontos por Produto da Venda"
				filterTablePrint={<FilterTablePrint />}
				landscape
			>
				<ProductPointsList />
			</ReportList>
		</Permission>
	);
};

export default ReportProductPoints;
